import React, { useEffect, useRef } from 'react';
import { Message } from '../../types';
import './MessageContextMenu.css';

interface MessageContextMenuProps {
  message: Message;
  isOperator: boolean;
  x: number;
  y: number;
  onReply?: (message: Message) => void;
  onEdit?: (message: Message) => void;
  onPin?: (message: Message) => void;
  onDelete?: (messageId: number) => void;
  onQuote?: (message: Message) => void;
  onClose: () => void;
}

const MessageContextMenu: React.FC<MessageContextMenuProps> = ({
  message,
  isOperator,
  x,
  y,
  onReply,
  onEdit,
  onPin,
  onDelete,
  onQuote,
  onClose
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Закрывать меню при клике снаружи
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    // Закрывать меню по клавише Escape
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  // Вычисляем позицию, чтобы меню не выходило за края экрана
  useEffect(() => {
    if (menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let adjustedX = x;
      let adjustedY = y;

      // Если меню выходит за правый край
      if (x + rect.width > viewportWidth) {
        adjustedX = viewportWidth - rect.width - 10;
      }

      // Если меню выходит за нижний край
      if (y + rect.height > viewportHeight) {
        adjustedY = viewportHeight - rect.height - 10;
      }

      menuRef.current.style.left = `${adjustedX}px`;
      menuRef.current.style.top = `${adjustedY}px`;
    }
  }, [x, y]);

  const handleAction = (action: () => void) => {
    action();
    onClose();
  };

  return (
    <div ref={menuRef} className="message-context-menu" style={{ left: x, top: y }}>
      <div className="context-menu-items">
        {/* Ответить - для всех сообщений */}
        {onReply && (
          <div
            className="context-menu-item"
            onClick={() => handleAction(() => onReply(message))}
          >
            <i className="bi bi-arrow-return-right"></i>
            <span>Ответить</span>
            <span className="shortcut">Ctrl+R</span>
          </div>
        )}

        {/* Цитата */}
        {onQuote && (
          <div
            className="context-menu-item"
            onClick={() => handleAction(() => onQuote(message))}
          >
            <i className="bi bi-quote"></i>
            <span>Цитата</span>
            <span className="shortcut">Ctrl+Q</span>
          </div>
        )}

        {/* Редактировать - только для операторов */}
        {isOperator && onEdit && (
          <div
            className="context-menu-item"
            onClick={() => handleAction(() => onEdit(message))}
          >
            <i className="bi bi-pencil-square"></i>
            <span>Редактировать</span>
            <span className="shortcut">Ctrl+E</span>
          </div>
        )}

        {/* Закрепить - только для операторов */}
        {isOperator && onPin && (
          <div
            className="context-menu-item"
            onClick={() => handleAction(() => onPin(message))}
          >
            <i className="bi bi-pin-angle-fill"></i>
            <span>Закрепить</span>
            <span className="shortcut">Ctrl+P</span>
          </div>
        )}

        {/* Разделитель */}
        <div className="context-menu-divider"></div>

        {/* Удалить - для всех */}
        {onDelete && (
          <div
            className="context-menu-item context-menu-item-danger"
            onClick={() => handleAction(() => onDelete(message.id))}
          >
            <i className="bi bi-trash3"></i>
            <span>Удалить</span>
            <span className="shortcut">Del</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageContextMenu;
